/**
 * Logica condivisa per il confronto tra periodo corrente e periodo di comparazione.
 * Usata dalla tabella (UI) e dal download CSV per includere colonne confronto e variazione %.
 */
import { ConnectorType, MetricsByConnectorType } from 'src/utils/gql/types';
import { TableRowsType } from './types';

export interface CompareData {
  shopify: any;
  magento?: any;
  facebook: any;
  googleAds: any;
  googleAnalytics4: any;
  googleSearchConsole?: any;
  kipify: any;
  customFormula?: any;
  /** MetricValuesRow[] da getDatabaseConnectorDataForCustomReport */
  database?: any;
}

function isMissingCompareRowEquivalentToZero(cell: TableRowsType): boolean {
  const hay = `${cell.id ?? ''} ${cell.title ?? ''}`.toLowerCase();

  if (cell.type === ConnectorType.GOOGlE_ANALYTICS4) {
    return /revenue|purchase|items|sessions|users|eventcount|views/.test(hay);
  }

  if (
    cell.type === ConnectorType.SHOPIFY ||
    cell.type === ConnectorType.MAGENTO
  ) {
    return /sales|revenue|orders|customers|cost|refund|tax|shipping|discount|quantity/.test(
      hay,
    );
  }

  if (cell.type === ConnectorType.FACEBOOK) {
    return /spend|amount|impressions|clicks|reach|actions|conversions/.test(
      hay,
    );
  }

  if (cell.type === ConnectorType.GOOGLE_ADS) {
    return /cost|spend|impressions|clicks|conversions|conversion.*value|revenue/.test(
      hay,
    );
  }

  if (cell.type === ConnectorType.GOOGLE_SEARCH_CONSOLE) {
    return /clicks|impressions/.test(hay);
  }

  // Metriche DB tipicamente additive (count/sum): riga assente nel compare ≈ 0
  if (cell.type === ConnectorType.DATABASE) {
    return true;
  }

  return false;
}

/**
 * Stessa normalizzazione del join Vendor (Shopify) ↔ Product Brand (Google Ads).
 * Serve anche nel match del periodo di confronto: le righe UI usano il label
 * canonico Shopify, mentre i dati compare Google Ads restano grezzi.
 */
export function normalizeVendorBrandValue(raw: string): string {
  if (!raw) return '';
  let value = String(raw).trim();
  value = value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  value = value
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .toLowerCase();
  value = value.replace(/\s{2,}/g, ' ');
  return value;
}

function isVendorOrBrandSplitKey(key: string): boolean {
  const k = String(key || '').toLowerCase();
  return (
    k === 'vendor' ||
    k === 'product_brand' ||
    k === 'segments.product_brand' ||
    k.endsWith('.product_brand')
  );
}

export function normalizeSplitValueForMatch(key: string, value: string): string {
  const k = String(key || '').toLowerCase();
  const v = String(value ?? '');
  if (!v) return '';
  // Allinea compare a join Vendor↔Brand (case/accenti/simboli), altrimenti
  // Spend Google Ads matcha 0 sulle righe e solo il Total (sum loose) resta corretto.
  if (isVendorOrBrandSplitKey(k)) {
    return normalizeVendorBrandValue(v);
  }
  if (k === 'product_type' || k === 'product_title') {
    return v
      .replace(/\s{2,}/g, ' ')
      .trim()
      .toLowerCase();
  }
  if (k === 'date') return v.split('T')[0].trim();
  if (k.includes('country')) return v.trim().toUpperCase();
  // GSC query/page: match case-sensitive sui path ma trim
  if (k === 'query' || k === 'page' || k === 'searchappearance') {
    return v.trim();
  }
  return v.trim();
}

/** Chiave stabile split per lookup O(1) nel compare (evita freeze su migliaia di righe). */
export function buildCompareSplitLookupKey(
  splitValues: Array<{ key?: string; value?: string } | null | undefined>,
): string {
  return (splitValues || [])
    .filter((sv): sv is { key?: string; value?: string } => !!sv && !!sv.key)
    .map(
      (sv) =>
        `${String(sv.key)}=${normalizeSplitValueForMatch(String(sv.key), String(sv.value ?? ''))}`,
    )
    .sort()
    .join('|');
}

export function buildCompareRowLookup(
  compareRows: any[] | null | undefined,
): Map<string, any> {
  const map = new Map<string, any>();
  if (!Array.isArray(compareRows)) return map;
  for (const row of compareRows) {
    const key = buildCompareSplitLookupKey(row?.splitValues || []);
    if (!map.has(key)) map.set(key, row);
  }
  return map;
}

export function calculateChange(
  currentValue: number | null,
  previousValue: number | null,
  metricKey?: string,
  connectorType?: ConnectorType | string,
): {
  percentage: number | null;
  value: number;
  isPositive: boolean;
  isInfinite?: boolean;
} | null {
  if (
    previousValue === null ||
    previousValue === undefined ||
    currentValue === null ||
    currentValue === undefined
  ) {
    return null;
  }
  const isInvertedMetric =
    connectorType === ConnectorType.KIPIFY &&
    metricKey &&
    (metricKey === 'oac' ||
      metricKey === 'acos' ||
      metricKey === 'acos_total' ||
      metricKey === 'cac' ||
      metricKey === 'cac_new_customers');

  if (previousValue === 0) {
    const rawIsPositive = currentValue >= 0;
    const isPositive = isInvertedMetric ? !rawIsPositive : rawIsPositive;
    return {
      percentage: null as number | null,
      value: previousValue,
      isPositive,
      isInfinite: true as const,
    };
  }

  const percentage = ((currentValue - previousValue) / previousValue) * 100;
  const rawIsPositive = percentage >= 0;
  const isPositive = isInvertedMetric ? !rawIsPositive : rawIsPositive;
  return {
    percentage: Math.round(percentage * 100) / 100,
    value: previousValue,
    isPositive,
  };
}

/**
 * Restituisce il valore del periodo di confronto per la cella (metrica) nella riga data.
 * Usata per la tabella e per il CSV.
 */
export function findCompareValue(
  cell: TableRowsType,
  row: TableRowsType[],
  compareData: CompareData | null | undefined,
): number | null {
  if (!compareData || !cell || cell.isSplit) return null;

  const connectorType = cell.type;
  let compareRows: any[] = [];

  if (connectorType === ConnectorType.SHOPIFY && compareData.shopify) {
    const shopifyData = compareData.shopify;
    if (
      shopifyData &&
      typeof shopifyData === 'object' &&
      'metricRows' in shopifyData
    ) {
      compareRows = Array.isArray(shopifyData.metricRows)
        ? shopifyData.metricRows
        : [];
    } else if (Array.isArray(shopifyData)) {
      compareRows = shopifyData;
    }
  } else if (connectorType === ConnectorType.MAGENTO && compareData.magento) {
    const magentoData = compareData.magento;
    if (
      magentoData &&
      typeof magentoData === 'object' &&
      'metricRows' in magentoData
    ) {
      compareRows = Array.isArray(magentoData.metricRows)
        ? magentoData.metricRows
        : [];
    } else if (Array.isArray(magentoData)) {
      compareRows = magentoData;
    }
  } else if (connectorType === ConnectorType.FACEBOOK && compareData.facebook) {
    const facebookData = compareData.facebook;
    compareRows = Array.isArray(facebookData) ? facebookData : [];
  } else if (
    connectorType === ConnectorType.GOOGLE_ADS &&
    compareData.googleAds
  ) {
    const googleAdsData = compareData.googleAds;
    compareRows = Array.isArray(googleAdsData) ? googleAdsData : [];
  } else if (
    connectorType === ConnectorType.GOOGlE_ANALYTICS4 &&
    compareData.googleAnalytics4
  ) {
    const googleAnalyticsData = compareData.googleAnalytics4;
    compareRows = Array.isArray(googleAnalyticsData) ? googleAnalyticsData : [];
  } else if (
    connectorType === ConnectorType.GOOGLE_SEARCH_CONSOLE &&
    compareData.googleSearchConsole
  ) {
    const gscData = compareData.googleSearchConsole;
    compareRows = Array.isArray(gscData) ? gscData : [];
  } else if (connectorType === ConnectorType.KIPIFY && compareData.kipify) {
    const kipifyData = compareData.kipify;
    compareRows = Array.isArray(kipifyData) ? kipifyData : [];
  } else if (
    connectorType === ConnectorType.DATABASE &&
    compareData.database
  ) {
    const databaseData = compareData.database;
    compareRows = Array.isArray(databaseData) ? databaseData : [];
  } else if (
    (connectorType as any) === MetricsByConnectorType.CUSTOM_FORMULA &&
    (compareData as any)?.customFormula
  ) {
    const customFormulaCompare = (compareData as any).customFormula;
    compareRows = Array.isArray(customFormulaCompare)
      ? customFormulaCompare
      : [];
  }

  if (!Array.isArray(compareRows) || compareRows.length === 0) return null;

  const currentSplits = row
    .filter((c) => {
      if (!c || !c.isSplit) return false;
      if (c.type === connectorType) return true;
      const shared =
        c.id === 'product_type' ||
        c.id === 'product_title' ||
        c.id === 'product_brand' ||
        c.id === 'segments.product_brand' ||
        c.id === 'vendor' ||
        c.id === 'date' ||
        c.id === 'kipify_country';
      if (!shared) return false;
      if (
        connectorType === ConnectorType.GOOGLE_ADS ||
        connectorType === ConnectorType.FACEBOOK ||
        connectorType === ConnectorType.KIPIFY ||
        connectorType === ConnectorType.SHOPIFY
      )
        return true;
      return false;
    })
    .map((c) => {
      let normalizedKey = c!.id;
      if (
        connectorType === ConnectorType.GOOGLE_ADS &&
        c!.id.startsWith('segments.')
      ) {
        normalizedKey = c!.id.replace(/^segments\./, '').replace(/_l\d+$/, '');
      }
      return {
        key: normalizedKey,
        originalKey: c!.id,
        value: normalizeSplitValueForMatch(
          normalizedKey,
          String(c!.value ?? ''),
        ),
        type: c!.type,
      };
    });

  const normalizeCompareSplitKey = (k: string): string => {
    const key = String(k || '');
    if (
      connectorType === ConnectorType.GOOGLE_ADS &&
      key.startsWith('segments.')
    ) {
      return key.replace(/^segments\./, '').replace(/_l\d+$/, '');
    }
    return key;
  };
  const compareSplitKeySet = new Set<string>();
  for (const r of compareRows) {
    const splits = (r as any)?.splitValues || [];
    for (const sv of splits) {
      const rawKey = String(sv?.key ?? '');
      if (!rawKey) continue;
      compareSplitKeySet.add(rawKey);
      compareSplitKeySet.add(normalizeCompareSplitKey(rawKey));
    }
  }
  const relevantCurrentSplits = currentSplits.filter(
    (s) =>
      compareSplitKeySet.has(s.key) || compareSplitKeySet.has(s.originalKey),
  );

  const isKipifyRoasCell =
    !cell.isSplit &&
    (cell.id === 'roas' || cell.id === 'kipify_roas') &&
    (cell.type === ConnectorType.KIPIFY ||
      (cell.type as any) === MetricsByConnectorType.KIPIFY);
  if (isKipifyRoasCell) {
    const shopifyRevenueCells = row.filter(
      (c) =>
        c &&
        !c.isSplit &&
        c.type === ConnectorType.SHOPIFY &&
        /gross.*sales|total.*sales|net.*sales|revenue|sales/i.test(
          c.id || c.title || '',
        ),
    );
    const spendCells = row.filter(
      (c) =>
        c &&
        !c.isSplit &&
        ((c.type === ConnectorType.FACEBOOK &&
          /spend|amount_spent|amount/i.test(c.id || c.title || '')) ||
          (c.type === ConnectorType.GOOGLE_ADS &&
            /cost|spend|advertising_spend/i.test(c.id || c.title || '')) ||
          ((c.type === ConnectorType.KIPIFY ||
            (c.type as any) === MetricsByConnectorType.KIPIFY) &&
            /total_marketing_spend/i.test(c.id || c.title || ''))),
    );
    let compareRevenue = 0;
    for (const revCell of shopifyRevenueCells) {
      const v = findCompareValue(revCell!, row, compareData);
      if (v !== null && v !== undefined && !isNaN(Number(v)))
        compareRevenue += Number(v);
    }
    let compareSpend = 0;
    for (const sCell of spendCells) {
      const v = findCompareValue(sCell!, row, compareData);
      if (v !== null && v !== undefined && !isNaN(Number(v)))
        compareSpend += Number(v);
    }
    if (compareSpend > 0) {
      const roas = compareRevenue / compareSpend;
      return isFinite(roas) ? Number(roas.toFixed(2)) : null;
    }
    return null;
  }

  const metricKeysToMatch = [cell.id];
  if (
    connectorType === ConnectorType.KIPIFY &&
    cell.id &&
    !metricKeysToMatch.includes(cell.id)
  ) {
    metricKeysToMatch.push(cell.id);
  }
  if (connectorType === ConnectorType.GOOGLE_ADS) {
    const key = cell.id.toLowerCase();
    if (key === 'cost' || key === 'spend')
      metricKeysToMatch.push(
        'metrics.cost_micros',
        'cost_micros',
        'metrics.cost',
        'cost',
      );
    else if (key === 'impressions')
      metricKeysToMatch.push('metrics.impressions', 'impressions');
    else if (key === 'clicks')
      metricKeysToMatch.push('metrics.clicks', 'clicks');
    else if (key === 'conversions')
      metricKeysToMatch.push('metrics.conversions', 'conversions');
    else if (key === 'conversions_value' || key === 'revenue')
      metricKeysToMatch.push(
        'metrics.conversions_value',
        'conversions_value',
        'metrics.revenue',
        'revenue',
      );
    else if (key === 'roas') metricKeysToMatch.push('metrics.roas', 'roas');
    else if (key === 'average_cpc' || key === 'cpc')
      metricKeysToMatch.push(
        'metrics.average_cpc',
        'average_cpc',
        'metrics.cpc',
        'cpc',
      );
    else if (key === 'average_cpm' || key === 'cpm')
      metricKeysToMatch.push(
        'metrics.average_cpm',
        'average_cpm',
        'metrics.cpm',
        'cpm',
      );
    else if (key === 'ctr') metricKeysToMatch.push('metrics.ctr', 'ctr');
    if (!cell.id.startsWith('metrics.'))
      metricKeysToMatch.push(`metrics.${cell.id}`);
    if (cell.id.startsWith('metrics.'))
      metricKeysToMatch.push(cell.id.replace(/^metrics\./, ''));
  }

  const compareRow = compareRows.find((r: any) => {
    const hasMetric = r.metricValues?.some(
      (mv: any) =>
        metricKeysToMatch.includes(mv.key) ||
        mv.key === cell.id ||
        (connectorType === ConnectorType.GOOGLE_ADS &&
          mv.key === `metrics.${cell.id}`),
    );
    if (!hasMetric) return false;
    if (relevantCurrentSplits.length === 0) return true;
    const compareSplits = (r.splitValues || []).map((sv: any) => {
      let normalizedKey = sv.key;
      if (
        connectorType === ConnectorType.GOOGLE_ADS &&
        typeof sv.key === 'string' &&
        sv.key.startsWith('segments.')
      ) {
        normalizedKey = sv.key.replace(/^segments\./, '').replace(/_l\d+$/, '');
      }
      return {
        key: normalizedKey,
        originalKey: sv.key,
        value: normalizeSplitValueForMatch(
          normalizedKey,
          String(sv.value ?? ''),
        ),
      };
    });
    if (
      connectorType === ConnectorType.KIPIFY &&
      compareSplits.length === 0 &&
      relevantCurrentSplits.length > 0
    )
      return true;
    const allSplitsMatch = relevantCurrentSplits.every((currentSplit) =>
      compareSplits.some(
        (compareSplit: { key: string; originalKey: string; value: string }) =>
          (compareSplit.key === currentSplit.key ||
            compareSplit.originalKey === currentSplit.originalKey) &&
          compareSplit.value === currentSplit.value,
      ),
    );
    return allSplitsMatch;
  });

  if (compareRow) {
    const compareMetric = compareRow.metricValues?.find(
      (mv: any) =>
        metricKeysToMatch.includes(mv.key) ||
        mv.key === cell.id ||
        (connectorType === ConnectorType.GOOGLE_ADS &&
          mv.key === `metrics.${cell.id}`),
    );
    if (compareMetric) {
      const numValue = Number(compareMetric.value);
      return !isNaN(numValue) ? numValue : null;
    }
  }

  if (!compareRow && relevantCurrentSplits.length > 0) {
    return isMissingCompareRowEquivalentToZero(cell) ? 0 : null;
  }

  if (
    connectorType === ConnectorType.KIPIFY &&
    relevantCurrentSplits.length > 0
  ) {
    const fallbackRow = compareRows.find((r: any) =>
      r.metricValues?.some(
        (mv: any) => metricKeysToMatch.includes(mv.key) || mv.key === cell.id,
      ),
    );
    if (fallbackRow) {
      const compareMetric = fallbackRow.metricValues?.find(
        (mv: any) => metricKeysToMatch.includes(mv.key) || mv.key === cell.id,
      );
      if (compareMetric) {
        const numValue = Number(compareMetric.value);
        return !isNaN(numValue) ? numValue : null;
      }
    }
  }

  if (connectorType === ConnectorType.KIPIFY) {
    const anyRowWithMetric = compareRows.find((r: any) => {
      const hasMetric = r.metricValues?.some(
        (mv: any) => metricKeysToMatch.includes(mv.key) || mv.key === cell.id,
      );
      if (!hasMetric) return false;
      if (currentSplits.length === 0) return true;
      const compareSplits = (r.splitValues || []).map((sv: any) => ({
        key: sv.key,
        value: String(sv.value ?? ''),
      }));
      if (compareSplits.length === 0) return true;
      return currentSplits.every((currentSplit) =>
        compareSplits.some(
          (compareSplit: { key: string; value: string }) =>
            compareSplit.key === currentSplit.key &&
            compareSplit.value === currentSplit.value,
        ),
      );
    });
    if (anyRowWithMetric) {
      const compareMetric = anyRowWithMetric.metricValues?.find(
        (mv: any) => metricKeysToMatch.includes(mv.key) || mv.key === cell.id,
      );
      if (compareMetric) {
        const numValue = Number(compareMetric.value);
        return !isNaN(numValue) ? numValue : null;
      }
    }
  }

  return null;
}
